import React, { lazy, useState, Suspense, useEffect } from 'react'
import createStore from 'teaful'
import { Affix, Menu, Button, Dropdown } from 'antd'
import { ContextToolbar } from '@guillotinaweb/react-gmi'
import { PanelPermissions } from '@guillotinaweb/react-gmi'
import { useTraversal, useLocation } from '@guillotinaweb/react-gmi'
import { PanelItems } from '@guillotinaweb/react-gmi'

import { DownOutlined, EyeOutlined } from '@ant-design/icons'

import useSaveCellar from './useSaveCellar'
import { AuditLogTab } from '../../../common/auditlog'
import { ContentReviewTab } from '../../../components/content-review-tab'
import { SEOTab } from '../../../components/seo-tab'
import { SITES, SITES_INFO } from '../../../consts'
import { TabsPanelWithLock } from '../../../components/tab-panel-with-lock'
import { getDomain, getCellarMask } from '../../../utils'

import './styles.css'

export const { useStore, getStore } = createStore({})

const QUERY_PARAM = 'menu'
const loadComponent = (key) =>
  lazy(() =>
    import(`./partials/${key}`).catch(() => ({ default: () => 'No content' }))
  )

function CellarEditor() {
  const [location, setLocation] = useLocation()
  const defaultItem = location.get('tab') === 'Images' ? 'images' : 'details'
  const item = location.get(QUERY_PARAM) || defaultItem
  const ctx = useTraversal()
  const cellar = ctx.state.context
  const [Comp, setComp] = useState()
  const [key, setKey] = useState(item)
  const [saving, save] = useSaveCellar()

  function onClick(e) {
    setLocation({ [QUERY_PARAM]: e.key })
    setComp(loadComponent(e.key))
    setKey(e.key)
  }

  function onSave() {
    const [store] = getStore()
    save(store)
  }

  useEffect(() => {
    const [, setStore] = getStore()
    setStore({})
    setComp(loadComponent(item))
  }, [])

  const menu = (
    <Menu>
      {SITES.map((site) => {
        const lang = SITES_INFO[site].lang
        const url = `https://${getDomain(site)}${getCellarMask(
          cellar['@name'],
          lang
        )}`
        return (
          <Menu.Item key={site} onClick={() => window.open(url, '_blank')}>
            {site}
          </Menu.Item>
        )
      })}
    </Menu>
  )

  return (
    <div className="product-editor" key={key}>
      <Affix>
        <div
          style={{
            backgroundColor: 'white',
            display: 'flex',
            padding: 10,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h1 className="title is-size-4 is-primary">{cellar.nombre}</h1>
          <div>
            <Dropdown overlay={menu}>
              <Button>
                <EyeOutlined /> View winery on <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" disabled={saving} onClick={onSave}>
              {saving ? 'Saving...' : 'Save winery'}
            </Button>
          </div>
        </div>
      </Affix>
      <div style={{ display: 'flex' }}>
        <Affix offsetTop={50}>
          <Menu
            onClick={onClick}
            style={{ width: 256 }}
            defaultSelectedKeys={[item]}
            mode="inline"
          >
            <Menu.ItemGroup key="g0" title="Id, name...">
              <Menu.Item
                key="details"
                icon={<i className="icon icon-bodega" />}
              >
                Winery details
              </Menu.Item>
            </Menu.ItemGroup>

            <Menu.ItemGroup key="g1" title="Bodega page sections">
              <Menu.Item
                key="origins"
                icon={<i className="icon icon-cellar" />}
              >
                Origins and history
              </Menu.Item>
              <Menu.Item key="team" icon={<i className="icon icon-team" />}>
                The team
              </Menu.Item>
              <Menu.Item
                key="territory"
                icon={<i className="icon icon-vineyard" />}
              >
                Vineyards
              </Menu.Item>
              <Menu.Item
                key="elaboration"
                icon={<i className="icon icon-elaboration" />}
              >
                Winemaking
              </Menu.Item>
              <Menu.Item key="wines" icon={<i className="icon icon-wines" />}>
                The wines
              </Menu.Item>
              <Menu.Item
                key="company"
                icon={<i className="icon icon-company" />}
              >
                The company / group
              </Menu.Item>
              <Menu.Item
                key="location"
                icon={<i className="icon icon-marker" />}
              >
                Town
              </Menu.Item>
              <Menu.Item
                key="contact"
                icon={<i className="icon icon-mailbox" />}
              >
                Contact
              </Menu.Item>
            </Menu.ItemGroup>
          </Menu>
        </Affix>
        <div style={{ margin: 20, width: '100%', overflowX: 'auto' }}>
          {Comp && (
            <Suspense fallback={<div />}>
              <Comp cellar={cellar} />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  )
}

export function BodegaView(props) {
  const ctx = useTraversal()

  let tabs = {
    Items: PanelItems,
    Edit: CellarEditor,
    Permissions: PanelPermissions,
    AuditLog: AuditLogTab,
    SEO: SEOTab,
    ContentReview: ContentReviewTab,
  }

  const permissions = {
    Edit: 'guillotina.ModifyContent',
    Permissions: 'guillotina.SeePermissions',
    SEO: 'guillotina.ModifyContent',
    ContentReview: 'guillotina.ModifyContent',
  }

  const calculated = ctx.filterTabs(tabs, permissions)

  return (
    <TabsPanelWithLock
      tabs={calculated}
      currentTab="Items"
      editableTabs={['Edit', 'Permissions', 'SEO', 'ContentReview']}
      fallback={CellarEditor}
      rightToolbar={<ContextToolbar {...props} />}
      {...props}
    />
  )
}
